import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import {
  SettingsCard,
  SettingsCards,
  SettingsFooter,
  SettingsNote,
  SettingsPageHeader,
} from '../SettingsShell/SettingsPage';
import type { LocationNumber, PhoneData } from '../../settings-phone/types';
import { NUMBER_ROLE_LABEL, REGISTRATION_WORD } from '../../settings-phone/types';
import { callerIdMismatch } from '../../settings-phone/mockData';

export interface PhoneNumbersViewProps {
  data: PhoneData;
  location: string;
  /** Where the numbers are actually bought, ported and released. */
  accountAppHref?: string;
}

/**
 * Practice management › Phone numbers (spec 16 §4). **Two cards, entirely
 * read-only.**
 *
 * Nothing here can be edited — numbers are bought, ported and released in the
 * Account app and land here at the next sync. The page exists so somebody can
 * answer *"which number does ELVA answer?"* without leaving Settings, and so
 * that the one thing which fails silently (texting registration) is written down
 * somewhere.
 */
export function PhoneNumbersView({ data, location, accountAppHref = '#' }: PhoneNumbersViewProps) {
  const mismatch = callerIdMismatch(data.numbers);
  const used = data.numbers.filter((n) => n.usedByElva).length;
  const worthALook = mismatch ? 1 : 0;

  return (
    <div>
      <SettingsPageHeader
        eyebrow="Settings · Practice management"
        titleLead="Phone"
        titleAccent="numbers"
        sub={
          <>
            The numbers this location uses and{' '}
            <b className="font-semibold text-[var(--text-secondary)]">what ELVA does with each one</b>. Everything here
            is read-only — the numbers themselves are bought and ported in the Account app.
          </>
        }
      />

      <SettingsCards>
        <SettingsCard
          n="01"
          title="The numbers this location uses"
          sub={
            <>What each number is for.</>
          }
        >
          <SettingsNote tone="indigo">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <a
                href={accountAppHref}
                className="inline-flex shrink-0 items-center gap-1.5 text-[12.5px] font-semibold text-[var(--brand-indigo)] hover:underline"
              >
                Open the Account app
                <Icon kind="external" size={12} stroke={2} />
              </a>
            </div>
          </SettingsNote>

          {data.numbers.map((n, i) => (
            <NumberRow
              key={`${n.e164}-${n.role}`}
              number={n}
              last={i === data.numbers.length - 1}
              /* The amber block sits on the caller-ID row itself, because that is
                 the row somebody would be looking at when they wonder. */
              callerIdWarning={n.role === 'outbound_caller_id' && mismatch !== undefined}
            />
          ))}

          {mismatch ? (
            <SettingsNote tone="amber">
              The number patients see when ELVA calls isn&rsquo;t your main line, so fewer will pick up. Change it in the
              Account app.
            </SettingsNote>
          ) : null}
        </SettingsCard>

        <SettingsCard
          n="02"
          accent="green"
          title="What has to be true before ELVA can use them"
        >
          <ReadinessRow
            label="Voice"
            desc="Nothing to register. ELVA can place and answer calls."
            chip={{ text: 'Ready', tone: 'green' }}
          />
          <ReadinessRow
            last
            label="Texting — carrier registration"
            desc={
              <>
                US carriers require every business number to be registered before it may send texts (
                <b className="font-semibold text-[var(--text-secondary)]">A2P 10DLC</b>).{' '}
                {data.texting.status === 'approved'
                  ? 'Approved here, so SMS works.'
                  : 'Until this is approved, no text sent from this number reaches anybody.'}
              </>
            }
            chip={{
              text:
                data.texting.status === 'approved' && data.texting.approvedAt !== undefined
                  ? `Approved ${data.texting.approvedAt}`
                  : REGISTRATION_WORD[data.texting.status],
              tone: data.texting.status === 'approved' ? 'green' : data.texting.status === 'rejected' ? 'red' : 'amber',
            }}
          />

          {/* This card exists because of §2.2 — it is the only place the A2P
              failure mode is written down anywhere in the product. */}
          <SettingsNote tone="indigo">
            If it isn&rsquo;t approved, carriers reject every text from this number.
          </SettingsNote>
        </SettingsCard>
      </SettingsCards>

      <SettingsFooter
        location={location}
        left={
          <>
            {data.numbers.length} numbers · {used} used by ELVA · {worthALook} worth a look · synced from Account{' '}
            {data.syncedMinutesAgo} minutes ago
          </>
        }
      />
    </div>
  );
}

function NumberRow({
  number,
  last,
  callerIdWarning,
}: {
  number: LocationNumber;
  last: boolean;
  callerIdWarning: boolean;
}) {
  return (
    <div
      className="flex items-start gap-4 px-6 py-[16px]"
      style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <span
        className={cn(
          'mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#F1F3F9]',
          !number.usedByElva && 'opacity-60',
        )}
      >
        <Icon
          kind={number.role === 'text' ? 'chat' : number.role === 'fax' ? 'doc' : 'phone'}
          size={15}
          stroke={1.8}
          className="text-[var(--text-muted)]"
        />
      </span>

      <div className={cn('min-w-0 flex-1', !number.usedByElva && 'opacity-70')}>
        <div className="font-[var(--font-mono)] text-[14px] font-bold text-[var(--text-primary)]">
          {number.display}
        </div>
        <div className="text-[11.5px] font-semibold text-[var(--text-muted)]">{NUMBER_ROLE_LABEL[number.role]}</div>
        <div className="mt-1 max-w-[560px] text-[12.5px] leading-[1.6] text-[var(--text-secondary)]">
          {number.what}
        </div>
        {callerIdWarning ? (
          <div className="mt-2 max-w-[560px] rounded-[9px] bg-[#FBF3E4] px-3 py-2 text-[11.5px] leading-[1.6] text-[var(--warn-amber)]">
            <b className="font-semibold">Patients do not recognise this number, so fewer of them pick up.</b> This is
            not the number on the practice&rsquo;s cards, the website or the door.
          </div>
        ) : null}
      </div>

      <span
        className={cn(
          'mt-1 shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold',
          number.usedByElva ? 'bg-[#EEF6F2] text-[var(--money-green)]' : 'bg-[#F1F3F9] text-[var(--text-muted)]',
        )}
      >
        {number.usedByElva ? 'ELVA uses it' : 'Not used'}
      </span>
    </div>
  );
}

function ReadinessRow({
  label,
  desc,
  chip,
  last,
}: {
  label: string;
  desc: React.ReactNode;
  chip: { text: string; tone: 'green' | 'amber' | 'red' };
  last?: boolean;
}) {
  const tones = {
    green: 'bg-[#EEF6F2] text-[var(--money-green)]',
    amber: 'bg-[#FBF3E4] text-[var(--warn-amber)]',
    red: 'bg-[#FBEDEC] text-[var(--alert-red)]',
  } as const;
  return (
    <div
      className="flex items-start justify-between gap-6 px-6 py-[16px]"
      style={last ? undefined : { borderBottom: '1px solid #edf0f6' }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold text-[var(--text-primary)]">{label}</div>
        <div className="mt-1 max-w-[600px] text-[12px] leading-[1.6] text-[var(--text-muted)]">{desc}</div>
      </div>
      <span className={cn('mt-0.5 shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold', tones[chip.tone])}>
        {chip.text}
      </span>
    </div>
  );
}
